import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
	ArrowLeft,
	Camera,
	ChevronRight,
	Eye,
	LifeBuoy,
	MessageCircle,
	Mic,
	Minimize2,
	MonitorDot,
	Paperclip,
	Search,
	Send,
	StopCircle,
	Video,
	X,
} from 'lucide-react';

import { supportChatApi } from './supportChatApi';
import { SUPPORT_CHAT_OPEN_EVENT } from '../chat/helper';
import './supportChat.css';

const SUPPORT_MEDIA_CONTROLS_ENABLED = true;
const SUPPORT_MAX_ATTACHMENTS = 5;
const SUPPORT_MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;

const formatTime = (dateValue) => {
	if (!dateValue) return '';

	try {
		return new Intl.DateTimeFormat('en-CA', {
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		}).format(new Date(dateValue));
	} catch (error) {
		return '';
	}
};

const firstFilled = (...values) => values.find((value) => {
	if (value === null || value === undefined) return false;
	return String(value).trim() !== '';
}) || '';

const joinAddress = (...parts) => parts
	.map((part) => String(part || '').trim())
	.filter(Boolean)
	.join(', ');

const getInitialEnrollment = (profileData, pharmacyLoginData) => ({
	pharmacyName: firstFilled(
		pharmacyLoginData?.name,
		profileData?.pharmacy_name,
		profileData?.pharmacy?.name
	),
	contactEmail: firstFilled(
		pharmacyLoginData?.email,
		profileData?.email,
		profileData?.contact_email
	),
	contactPhone: firstFilled(
		pharmacyLoginData?.phone,
		profileData?.phone,
		profileData?.contact_phone
	),
	address: firstFilled(
		pharmacyLoginData?.full_address,
		pharmacyLoginData?.address2,
		joinAddress(
			pharmacyLoginData?.address,
			pharmacyLoginData?.city,
			pharmacyLoginData?.province,
			pharmacyLoginData?.zip_code
		),
		profileData?.address2,
		profileData?.address1
	),
});

const getDefaultSenderName = (profileData, pharmacyLoginData, senderName = '') => (
	firstFilled(
		senderName,
		profileData?.full_name,
		profileData?.name,
		(profileData?.first_name && profileData?.last_name)
			? `${String(profileData.first_name).trim()} ${String(profileData.last_name).trim()}`
			: '',
		profileData?.first_name,
		profileData?.last_name,
		pharmacyLoginData?.full_name,
		pharmacyLoginData?.name,
		pharmacyLoginData?.first_name,
		pharmacyLoginData?.last_name,
	)
);

const hasRequiredEnrollmentDetails = (form) => Boolean(
	String(form?.pharmacyName || '').trim()
	&& String(form?.contactEmail || '').trim()
);

const isSupportEnabled = (status) => status?.enabled === true || status?.configured === true;

const hasSupportAccount = (status) => Boolean(
	status?.enrolled === true
	|| status?.tenant_id
	|| status?.slug
	|| status?.hub_url
	|| status?.status === 'active'
);

const getErrorMessage = (error) => {
	if (!error) return '';
	if (error.status === 401) return 'Support access needs to be set up again.';
	if (error.status === 409) return 'This support conversation is closed. Please start a new one.';
	if (error.status === 502) return 'Support chat is temporarily unavailable.';
	if (error.status === 503) return 'Support chat is not available right now.';
	return error.message || 'Something went wrong. Please try again.';
};

const blobToFile = (blob, filename) => new File([blob], filename, { type: blob.type || 'video/webm' });

const canvasToPngFile = (canvas, filename) => new Promise((resolve, reject) => {
	canvas.toBlob((blob) => {
		if (!blob) {
			reject(new Error('Unable to capture snapshot.'));
			return;
		}

		resolve(new File([blob], filename, { type: 'image/png' }));
	}, 'image/png');
});

const streamToSnapshotFile = async (stream, filename) => {
	const video = document.createElement('video');
	video.muted = true;
	video.playsInline = true;
	video.srcObject = stream;

	await new Promise((resolve, reject) => {
		video.onloadedmetadata = resolve;
		video.onerror = () => reject(new Error('Unable to read the selected screen.'));
	});

	await video.play();

	const width = video.videoWidth || window.innerWidth;
	const height = video.videoHeight || window.innerHeight;
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext('2d');
	context.drawImage(video, 0, 0, width, height);

	return canvasToPngFile(canvas, filename);
};

const fileToAttachment = (file) => ({
	id: `${file.name}-${file.size}-${file.lastModified || Date.now()}`,
	file,
	name: file.name,
	mime_type: file.type,
	size: file.size,
});

const getAttachmentName = (attachment) => attachment?.filename || attachment?.name || 'Attachment';
const getAttachmentRawName = (attachment) => attachment?.filename || attachment?.name || '';
const getAttachmentType = (attachment) => attachment?.content_type || attachment?.mime_type || '';
const getAttachmentSize = (attachment) => attachment?.size_bytes || attachment?.size || 0;
const isGenericAttachmentType = (contentType) => {
	const normalizedType = String(contentType || '').trim().toLowerCase();
	return !normalizedType || normalizedType === 'file' || normalizedType === 'application/octet-stream';
};

const getLowerAttachmentType = (attachment) => String(getAttachmentType(attachment) || '').trim().toLowerCase();
const getLowerAttachmentName = (attachment) => getAttachmentName(attachment).toLowerCase();
const hasSpecificAttachmentType = (attachment) => !isGenericAttachmentType(getAttachmentType(attachment));

const isNamedAudioAttachment = (attachment) => {
	const filename = getLowerAttachmentName(attachment);
	return filename.includes('audio-recording')
		|| filename.includes('voice-recording')
		|| filename.includes('voice-note')
		|| filename.includes('audio-note');
};

const isImageAttachment = (attachment) => {
	const contentType = getLowerAttachmentType(attachment);
	const filename = getLowerAttachmentName(attachment);
	if (hasSpecificAttachmentType(attachment)) {
		return contentType.startsWith('image/') || contentType === 'image';
	}
	return contentType.startsWith('image/')
		|| contentType === 'image'
		|| /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(filename);
};

const isPdfAttachment = (attachment) => {
	const contentType = getLowerAttachmentType(attachment);
	const filename = getLowerAttachmentName(attachment);
	if (hasSpecificAttachmentType(attachment)) {
		return contentType === 'application/pdf' || contentType === 'pdf';
	}
	return contentType === 'application/pdf'
		|| contentType === 'pdf'
		|| filename.endsWith('.pdf');
};

const isVideoAttachment = (attachment) => {
	const contentType = getLowerAttachmentType(attachment);
	const filename = getLowerAttachmentName(attachment);
	if (hasSpecificAttachmentType(attachment)) {
		return contentType.startsWith('video/') || contentType === 'video';
	}
	return !isNamedAudioAttachment(attachment) && (
		contentType.startsWith('video/')
		|| contentType === 'video'
		|| /\.(mp4|webm|ogg|mov|m4v|avi)$/i.test(filename)
	);
};

const isAudioAttachment = (attachment) => {
	const contentType = getLowerAttachmentType(attachment);
	const filename = getLowerAttachmentName(attachment);
	if (hasSpecificAttachmentType(attachment)) {
		return contentType.startsWith('audio/') || contentType === 'audio';
	}
	return contentType.startsWith('audio/')
		|| contentType === 'audio'
		|| isNamedAudioAttachment(attachment)
		|| /\.(mp3|wav|ogg|m4a|aac|webm)$/i.test(filename);
};

const normalizeMessageAttachments = (attachments = [], fallbackAttachments = [], attachmentMetadataByUrl = null) => (Array.isArray(attachments) ? attachments : [])
	.filter((attachment) => attachment?.url)
	.map((attachment, index) => {
		const fallbackAttachment = fallbackAttachments[index] || {};
		const cachedAttachment = attachmentMetadataByUrl?.get?.(attachment.url) || {};
		const rawType = getAttachmentType(attachment);
		const fallbackType = getAttachmentType(cachedAttachment) || getAttachmentType(fallbackAttachment);
		const rawName = getAttachmentRawName(attachment);
		const fallbackName = getAttachmentRawName(cachedAttachment) || getAttachmentRawName(fallbackAttachment);
		const rawSize = getAttachmentSize(attachment);
		const fallbackSize = getAttachmentSize(cachedAttachment) || getAttachmentSize(fallbackAttachment);

		return {
			url: attachment.url,
			filename: rawName || fallbackName || 'Attachment',
			content_type: isGenericAttachmentType(rawType) ? fallbackType || rawType : rawType,
			size_bytes: rawSize || fallbackSize,
		};
	});

// The page link is only for the support team, so hide it from the pharmacy's own view.
const hideTicketPageUrl = (text) => (
	typeof text === 'string' ? text.replace(/(?:^|\n\n)Page: https?:\/\/\S+\s*$/, '') : text
);

const getLocalAttachmentPreviewUrl = (attachment) => {
	if (!attachment?.file) return '';

	try {
		return URL.createObjectURL(attachment.file);
	} catch (error) {
		return '';
	}
};

const toLocalMessageAttachments = (attachments = []) => attachments
	.map((attachment) => ({
		url: getLocalAttachmentPreviewUrl(attachment),
		filename: getAttachmentRawName(attachment) || attachment?.name || 'Attachment',
		content_type: getAttachmentType(attachment) || attachment?.file?.type || '',
		size_bytes: getAttachmentSize(attachment) || attachment?.file?.size || 0,
	}))
	.filter((attachment) => attachment.url);

const isTeamUser = (teamStatus) => {
	const normalizedStatus = String(teamStatus ?? '').toLowerCase();
	return teamStatus === true || Number(teamStatus) === 1 || normalizedStatus === 'true';
};

const openCurrentAttachment = (attachment) => {
	if (!attachment?.file) return;

	const previewUrl = URL.createObjectURL(attachment.file);
	const previewWindow = window.open(previewUrl, '_blank', 'noopener,noreferrer');

	if (!previewWindow) {
		URL.revokeObjectURL(previewUrl);
		return;
	}

	window.setTimeout(() => URL.revokeObjectURL(previewUrl), 60000);
};

const getValidFiles = (files, currentCount, onToolNotice) => {
	const selectedFiles = Array.from(files || []);
	if (!selectedFiles.length) return [];

	const remainingSlots = SUPPORT_MAX_ATTACHMENTS - currentCount;
	if (remainingSlots <= 0) {
		onToolNotice?.(`You can attach up to ${SUPPORT_MAX_ATTACHMENTS} files per message.`);
		return [];
	}

	const acceptedFiles = [];
	for (const file of selectedFiles) {
		if (acceptedFiles.length >= remainingSlots) break;

		if (file.size > SUPPORT_MAX_ATTACHMENT_SIZE) {
			onToolNotice?.(`${file.name} is larger than 25 MB.`);
			continue;
		}

		acceptedFiles.push(file);
	}

	if (selectedFiles.length > remainingSlots) {
		onToolNotice?.(`Only ${SUPPORT_MAX_ATTACHMENTS} files can be attached per message.`);
	}

	return acceptedFiles;
};

const AttachmentList = ({ attachments, onRemove, canViewCurrentFile = false }) => {
	if (!attachments.length) return null;

	return (
		<div className="support-chat-attachments">
			{attachments.map((attachment, index) => (
				<div className="support-chat-attachment" key={attachment.id || attachment.url || `${attachment.name}-${index}`}>
					<Paperclip size={14} />
					<span title={attachment.name}>{attachment.name}</span>
					{canViewCurrentFile && attachment.file && (
						<button
							type="button"
							className="support-chat-attachment-action"
							onClick={() => openCurrentAttachment(attachment)}
							aria-label={`View ${attachment.name || 'attachment'}`}
							title="View file"
						>
							<Eye size={13} />
						</button>
					)}
					<button
						type="button"
						className="support-chat-attachment-action"
						onClick={() => onRemove(index)}
						aria-label="Remove attachment"
						title="Remove file"
					>
						x
					</button>
				</div>
			))}
		</div>
	);
};

const VideoMessageAttachment = ({ url }) => {
	return (
		<div className="support-chat-message-media">
			<video className="support-chat-message-video" controls preload="metadata">
				<source src={url} />
			</video>
		</div>
	);
};

const MessageAttachment = ({ attachment }) => {
	const url = attachment?.url;
	if (!url) return null;

	const filename = getAttachmentName(attachment);

	if (isImageAttachment(attachment)) {
		return (
			<div className="support-chat-message-media support-chat-message-media--image">
				<a className="support-chat-message-image-link" href={url} target="_blank" rel="noopener noreferrer">
					<img className="support-chat-message-image" src={url} alt={filename} />
				</a>
			</div>
		);
	}

	if (isVideoAttachment(attachment)) {
		return <VideoMessageAttachment url={url} />;
	}

	if (isAudioAttachment(attachment)) {
		return (
			<div className="support-chat-message-media support-chat-message-media--audio">
				<audio className="support-chat-message-audio" controls preload="metadata">
					<source src={url} />
				</audio>
			</div>
		);
	}

	if (isPdfAttachment(attachment)) {
		return (
			<div className="support-chat-message-media">
				<iframe
					className="support-chat-message-pdf"
					src={url}
					title={filename}
				/>
			</div>
		);
	}

	return (
		<a className="support-chat-message-file" href={url} target="_blank" rel="noopener noreferrer">
			<span className="support-chat-message-file-icon">FILE</span>
		</a>
	);
};

const MessageAttachments = ({ attachments }) => {
	const normalized = normalizeMessageAttachments(attachments);
	if (!normalized.length) return null;

	return (
		<div className="support-chat-message-attachments">
			{normalized.map((attachment, index) => (
				<MessageAttachment
					key={attachment.url || `${attachment.filename}-${index}`}
					attachment={attachment}
				/>
			))}
		</div>
	);
};

const EnrollmentForm = ({ profileData, pharmacyLoginData, loading, error, onSubmit }) => {
	const [form, setForm] = useState(() => getInitialEnrollment(profileData, pharmacyLoginData));

	useEffect(() => {
		const nextForm = getInitialEnrollment(profileData, pharmacyLoginData);
		setForm((current) => ({
			pharmacyName: current.pharmacyName || nextForm.pharmacyName,
			contactEmail: current.contactEmail || nextForm.contactEmail,
			contactPhone: current.contactPhone || nextForm.contactPhone,
			address: current.address || nextForm.address,
		}));
	}, [profileData, pharmacyLoginData]);

	const updateField = (field, value) => {
		setForm((current) => ({ ...current, [field]: value }));
	};

	const submit = (event) => {
		event.preventDefault();
		onSubmit(form);
	};

	return (
		<form className="support-chat-enrollment" onSubmit={submit}>
			<div className="support-chat-enrollment-header">
				<div className="support-chat-empty-icon">
					<LifeBuoy size={20} />
				</div>
				<div>
					<h4>Connect to Support</h4>
					<p>Confirm pharmacy details to start support tickets.</p>
				</div>
			</div>
			<label>
				<span>Pharmacy name</span>
				<input
					value={form.pharmacyName}
					onChange={(event) => updateField('pharmacyName', event.target.value)}
					placeholder="Pharmacy name"
					required
				/>
			</label>
			<label>
				<span>Contact email</span>
				<input
					value={form.contactEmail}
					onChange={(event) => updateField('contactEmail', event.target.value)}
					placeholder="Contact email"
					type="email"
					required
				/>
			</label>
			<label>
				<span>Contact phone</span>
				<input
					value={form.contactPhone}
					onChange={(event) => updateField('contactPhone', event.target.value)}
					placeholder="Contact phone"
				/>
			</label>
			<label>
				<span>Address</span>
				<input
					value={form.address}
					onChange={(event) => updateField('address', event.target.value)}
					placeholder="Address"
				/>
			</label>
			{error && <p className="support-chat-error">{error}</p>}
			<div className="support-chat-enrollment-actions">
				<button type="submit" disabled={loading}>
					{loading ? 'Setting up...' : 'Get started'}
				</button>
			</div>
		</form>
	);
};

const SupportViewHeader = ({ title, status, onBack, onMinimize }) => (
	<div className="support-chat-pane-header support-chat-pane-header--compact">
		<button type="button" onClick={onBack} aria-label="Back to tickets">
			<ArrowLeft size={16} />
		</button>
		<div className="support-chat-pane-header-copy">
			<div className="support-chat-pane-header-row">
				<h4>{title}</h4>
				{status && (
					<span className={`support-chat-status ${status === 'closed' ? 'closed' : ''}`}>
						{status === 'closed' ? 'Closed' : 'Open'}
					</span>
				)}
			</div>
		</div>
		{onMinimize && (
			<button
				type="button"
				className="support-chat-pane-minimize-button"
				onClick={onMinimize}
				aria-label="Minimize support chat"
				title="Minimize"
			>
				<Minimize2 size={15} />
			</button>
		)}
	</div>
);

const ThreadList = ({
	threads,
	broadcasts,
	activeThreadId,
	onSelectThread,
	loading,
	unreadThreadIds = [],
}) => {
	const [searchQuery, setSearchQuery] = useState('');
	const unreadThreadIdSet = useMemo(
		() => new Set(unreadThreadIds.map((threadId) => String(threadId))),
		[unreadThreadIds],
	);

	const filteredThreads = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		if (!query) return threads;
		return threads.filter((thread) => {
			const title = (thread.title || '').toLowerCase();
			const preview = (thread.last_message || '').toLowerCase();
			return title.includes(query) || preview.includes(query);
		});
	}, [threads, searchQuery]);

	return (
		<div className="support-chat-home">
			{broadcasts.length > 0 && (
				<div className="support-chat-home-section">
					{broadcasts.slice(0, 2).map((broadcast) => (
						<div key={broadcast.id} className={`support-chat-notice ${broadcast.severity || 'info'}`}>
							<div className="support-chat-notice-title">{broadcast.title}</div>
							<div className="support-chat-notice-body">{broadcast.body}</div>
						</div>
					))}
				</div>
			)}

			{threads.length > 0 && (
				<div className="support-chat-search-wrap">
					<div className="support-chat-search">
						<Search size={12} className="support-chat-search-icon" aria-hidden="true" />
						<input
							type="text"
							className="support-chat-search-input focus:ring-0 focus:border-0 focus:outline-none"
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.target.value)}
							placeholder="Search tickets..."
							aria-label="Search tickets"
							autoComplete="off"
						/>
						{searchQuery && (
							<button
								type="button"
								className="support-chat-search-clear"
								onClick={() => setSearchQuery('')}
								aria-label="Clear search"
							>
								<X size={12} />
							</button>
						)}
					</div>
				</div>
			)}

			{loading ? (
				<div className="support-chat-loading">Loading...</div>
			) : threads.length === 0 ? (
				<div className="support-chat-muted-box">No tickets yet. Tap New ticket above.</div>
			) : filteredThreads.length === 0 ? (
				<div className="support-chat-muted-box">No matches.</div>
			) : (
				<div className="support-chat-thread-list">
					{filteredThreads.map((thread) => {
						const hasUnread = unreadThreadIdSet.has(String(thread.id));

						return (
							<button
								key={thread.id}
								className={`support-chat-thread ${activeThreadId === thread.id ? 'is-active' : ''} ${hasUnread ? 'has-unread' : ''}`}
								onClick={() => onSelectThread(thread.id)}
								type="button"
							>
								<div className="support-chat-thread-copy">
									<div className="support-chat-thread-row">
										<span className="support-chat-thread-title">
											{hasUnread && <span className="support-chat-thread-unread-dot" aria-label="Unread support reply" />}
											{thread.title || 'Support ticket'}
										</span>
										<span className={`support-chat-status ${thread.status === 'resolved' ? 'closed' : ''}`}>
											{thread.status === 'resolved' ? 'Closed' : 'Open'}
										</span>
									</div>
									<div className="support-chat-thread-meta-line">
										<span className="support-chat-thread-preview">{hideTicketPageUrl(thread.last_message) || 'No messages'}</span>
										<span className="support-chat-thread-date">{formatTime(thread.last_message_at || thread.created_at)}</span>
									</div>
								</div>
								<ChevronRight size={13} className="support-chat-thread-chevron" />
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};

const NewThreadForm = ({ loading, error, onSubmit, onCaptureSnapshot, onToolNotice, canViewCurrentFile }) => {
	const [title, setTitle] = useState('');
	const [message, setMessage] = useState('');
	const [attachments, setAttachments] = useState([]);
	const [processingMedia, setProcessingMedia] = useState(false);
	const [recordingType, setRecordingType] = useState('');
	const recorderRef = useRef(null);
	const recorderChunksRef = useRef([]);
	const recorderStreamRef = useRef(null);
	const fileInputRef = useRef(null);

	const addAttachments = (files) => {
		setAttachments((current) => [
			...current,
			...getValidFiles(files, current.length, onToolNotice).map(fileToAttachment),
		]);
	};

	const removeAttachment = (index) => {
		setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index));
	};

	const submit = async (event) => {
		event.preventDefault();
		try {
			await onSubmit({ title, message, attachments });
		} catch {
			// Parent displays the error and keeps this draft intact.
		}
	};

	const captureSnapshot = async () => {
		setProcessingMedia(true);
		try {
			const file = await onCaptureSnapshot();
			addAttachments([file]);
		} catch (_) {
			// Picker cancelled or permission denied: handleCaptureSnapshot already showed a notice.
		} finally {
			setProcessingMedia(false);
		}
	};

	const startRecording = async (type) => {
		if (recordingType) return;

		if (!navigator.mediaDevices || typeof MediaRecorder === 'undefined') {
			onToolNotice?.('Recording is not supported in this browser.');
			return;
		}

		let stream;
		try {
			if (type === 'screen') {
				stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
			} else if (type === 'audio') {
				stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			} else {
				stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
			}
		} catch (recordingError) {
			onToolNotice?.('Recording permission was not granted.');
			return;
		}

		recorderChunksRef.current = [];
		recorderStreamRef.current = stream;
		const recorder = new MediaRecorder(stream);
		recorderRef.current = recorder;
		setRecordingType(type);

		recorder.ondataavailable = (event) => {
			if (event.data?.size) {
				recorderChunksRef.current.push(event.data);
			}
		};

		recorder.onstop = () => {
			const mimeType = type === 'audio' ? 'audio/webm' : 'video/webm';
			const blob = new Blob(recorderChunksRef.current, { type: mimeType });
			const file = blobToFile(blob, `${type}-recording-${Date.now()}.webm`);
			recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
			recorderRef.current = null;
			recorderStreamRef.current = null;
			setRecordingType('');
			addAttachments([file]);
		};

		recorder.start();
	};

	const stopRecording = () => {
		if (recorderRef.current && recorderRef.current.state !== 'inactive') {
			recorderRef.current.stop();
		}
	};

	useEffect(() => () => {
		recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
	}, []);

	return (
		<form className="support-chat-new-thread support-chat-inline-content" onSubmit={submit}>
			<div className="scroll-box relative support-chat-inline-scroll" style={{ background: 'white', overflowY: 'auto' }}>
				<div className="support-chat-ticket-form">
				<label>
					<span>Issue summary</span>
					<input
						value={title}
						onChange={(event) => setTitle(event.target.value)}
						placeholder="e.g. Label printer not printing"
					/>
				</label>
				<label>
					<span>What happened?</span>
					<textarea
						value={message}
						onChange={(event) => setMessage(event.target.value)}
						placeholder="Tell us what you were doing, what went wrong, and any error messages you saw."
					/>
				</label>
				</div>
				{SUPPORT_MEDIA_CONTROLS_ENABLED && (
					<>
						<AttachmentList
							attachments={attachments}
							onRemove={removeAttachment}
							canViewCurrentFile={canViewCurrentFile}
						/>
						<div className="support-chat-tools support-chat-tools-bottom mt-2">
							<button
								type="button"
								className="support-chat-file-button"
								title="Attach file"
								onClick={() => fileInputRef.current?.click()}
							>
								<Paperclip size={17} />
							</button>
							<input
								ref={fileInputRef}
								className="support-chat-file-input"
								type="file"
								multiple
								onChange={(event) => {
									addAttachments(event.target.files);
									event.target.value = '';
								}}
							/>
							<button type="button" onClick={captureSnapshot} title="Snapshot current page" disabled={processingMedia}>
								<Camera size={17} />
							</button>
							{recordingType ? (
								<button type="button" onClick={stopRecording} title="Stop recording">
									<StopCircle size={17} />
								</button>
							) : (
								<>
									<button type="button" onClick={() => startRecording('audio')} title="Voice recording" disabled={processingMedia}>
										<Mic size={17} />
									</button>
									<button type="button" onClick={() => startRecording('screen')} title="Screen recording" disabled={processingMedia}>
										<MonitorDot size={17} />
									</button>
									<button type="button" onClick={() => startRecording('video')} title="Video recording" disabled={processingMedia}>
										<Video size={17} />
									</button>
								</>
							)}
						</div>
					</>
				)}
				{error && <p className="support-chat-error">{error}</p>}
			</div>
			<button type="submit" className="support-chat-submit-button" disabled={loading || processingMedia || recordingType || (!message.trim() && attachments.length === 0)}>
				{loading || processingMedia ? 'Submitting...' : 'Submit ticket'}
			</button>
		</form>
	);
};

const MessagePane = ({
	thread,
	messages,
	loading,
	error,
	onSend,
	onRetryMessage,
	onCaptureSnapshot,
	onToolNotice,
	canViewCurrentFile,
}) => {
	const [message, setMessage] = useState('');
	const [attachments, setAttachments] = useState([]);
	const [processingMedia, setProcessingMedia] = useState(false);
	const [recordingType, setRecordingType] = useState('');
	const recorderRef = useRef(null);
	const recorderChunksRef = useRef([]);
	const recorderStreamRef = useRef(null);
	const fileInputRef = useRef(null);
	const messagesRef = useRef(null);
	const isClosed = thread?.status === 'resolved';

	useEffect(() => {
		if (messagesRef.current) {
			messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
		}
	}, [messages]);

	const submit = async (event) => {
		event.preventDefault();
		const trimmed = message.trim();
		if ((!trimmed && attachments.length === 0) || isClosed) return;
		setMessage('');
		setAttachments([]);
		onSend({ message: trimmed, attachments });
	};

	const addAttachments = (files) => {
		if (isClosed) return;
		setAttachments((current) => [
			...current,
			...getValidFiles(files, current.length, onToolNotice).map(fileToAttachment),
		]);
	};

	const removeAttachment = (index) => {
		setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index));
	};

	const captureSnapshot = async () => {
		if (isClosed) return;
		setProcessingMedia(true);
		try {
			const file = await onCaptureSnapshot();
			addAttachments([file]);
		} catch (_) {
			// Picker cancelled or permission denied: handleCaptureSnapshot already showed a notice.
		} finally {
			setProcessingMedia(false);
		}
	};

	const startRecording = async (type) => {
		if (isClosed || recordingType) return;

		if (!navigator.mediaDevices || typeof MediaRecorder === 'undefined') {
			onToolNotice?.('Recording is not supported in this browser.');
			return;
		}

		let stream;
		try {
			if (type === 'screen') {
				stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
			} else if (type === 'audio') {
				stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			} else {
				stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
			}
		} catch (recordingError) {
			onToolNotice?.('Recording permission was not granted.');
			return;
		}

		recorderChunksRef.current = [];
		recorderStreamRef.current = stream;
		const recorder = new MediaRecorder(stream);
		recorderRef.current = recorder;
		setRecordingType(type);

		recorder.ondataavailable = (event) => {
			if (event.data?.size) {
				recorderChunksRef.current.push(event.data);
			}
		};

		recorder.onstop = async () => {
			const mimeType = type === 'audio' ? 'audio/webm' : 'video/webm';
			const blob = new Blob(recorderChunksRef.current, { type: mimeType });
			const file = blobToFile(blob, `${type}-recording-${Date.now()}.webm`);
			recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
			recorderRef.current = null;
			recorderStreamRef.current = null;
			setRecordingType('');
			addAttachments([file]);
		};

		recorder.start();
	};

	const stopRecording = () => {
		if (recorderRef.current && recorderRef.current.state !== 'inactive') {
			recorderRef.current.stop();
		}
	};

	return (
		<div className="support-chat-message-pane support-chat-inline-content">
			<div className="support-chat-messages-wrap" ref={messagesRef}>
				{loading ? (
					<div className="support-chat-loading">Loading conversation...</div>
				) : messages.length === 0 ? (
					<div className="support-chat-empty centered">
						<MessageCircle size={20} />
						<p>No messages yet.</p>
					</div>
				) : (
					messages.map((item) => (
						<div
							key={item.id || `${item.created_at}-${item.text}`}
							className={`support-chat-message ${item.direction === 'pos' ? 'from-pos' : 'from-agent'}`}
						>
							{item.direction === 'agent' && item.sender_name && (
								<div className="support-chat-sender">{item.sender_name}</div>
							)}
							{hideTicketPageUrl(item.text) && <div className="support-chat-bubble">{hideTicketPageUrl(item.text)}</div>}
							<MessageAttachments attachments={item.attachments} />
							<div className={`support-chat-message-time ${item.local_status === 'failed' ? 'is-failed' : ''}`}>
								{item.local_status === 'sending'
									? 'Sending...'
									: item.local_status === 'failed'
										? 'Failed to send'
										: formatTime(item.created_at || item.sent_at)}
							</div>
							{item.local_status === 'failed' && (
								<button
									type="button"
									className="support-chat-message-retry"
									onClick={() => onRetryMessage?.(item)}
								>
									Resend
								</button>
							)}
						</div>
					))
				)}
			</div>

			<div className="support-chat-composer-footer">
				{isClosed && (
					<div className="support-chat-closed-banner">This ticket is closed. Start a new ticket if you need more help.</div>
				)}
				{error && <p className="support-chat-error footer-error">{error}</p>}
				{SUPPORT_MEDIA_CONTROLS_ENABLED && (
					<AttachmentList
						attachments={attachments}
						onRemove={removeAttachment}
						canViewCurrentFile={canViewCurrentFile}
					/>
				)}

				<form className="support-chat-composer" onSubmit={submit}>
					<div className="support-chat-composer-box">
						<textarea
							value={message}
							className="support-chat-composer-input"
							onChange={(event) => setMessage(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter' && !event.shiftKey) {
									event.preventDefault();
									event.currentTarget.form?.requestSubmit();
								}
							}}
							placeholder={isClosed ? 'This ticket is closed' : 'Type your reply to support...'}
							disabled={isClosed}
							rows={1}
						/>
						<button
							type="submit"
							className="support-chat-send-btn"
							disabled={isClosed || processingMedia || recordingType || (!message.trim() && attachments.length === 0)}
							title="Send"
							aria-label="Send message"
						>
							<Send size={16} />
						</button>
					</div>
					{SUPPORT_MEDIA_CONTROLS_ENABLED && (
						<div className="support-chat-tools support-chat-tools-bottom">
							<button
								type="button"
								className="support-chat-file-button"
								title="Attach file"
								disabled={isClosed || processingMedia}
								onClick={() => fileInputRef.current?.click()}
							>
								<Paperclip size={17} />
							</button>
							<input
								ref={fileInputRef}
								className="support-chat-file-input"
								type="file"
								multiple
								disabled={isClosed || processingMedia}
								onChange={(event) => {
									addAttachments(event.target.files);
									event.target.value = '';
								}}
							/>
							<button type="button" onClick={captureSnapshot} title="Snapshot current page" disabled={isClosed || processingMedia}>
								<Camera size={17} />
							</button>
							{recordingType ? (
								<button type="button" onClick={stopRecording} title="Stop recording">
									<StopCircle size={17} />
								</button>
							) : (
								<>
									<button type="button" onClick={() => startRecording('audio')} title="Voice recording" disabled={isClosed || processingMedia}>
										<Mic size={17} />
									</button>
									<button type="button" onClick={() => startRecording('screen')} title="Screen recording" disabled={isClosed || processingMedia}>
										<MonitorDot size={17} />
									</button>
									<button type="button" onClick={() => startRecording('video')} title="Video recording" disabled={isClosed || processingMedia}>
										<Video size={17} />
									</button>
								</>
							)}
						</div>
					)}
				</form>
			</div>
		</div>
	);
};

const SupportChatPanel = ({
	profileData,
	pharmacyLoginData,
	senderName,
	teamStatus,
	isActive = true,
	refreshToken = 0,
	registerToolbar,
	onSupportAvailabilityChange,
	onUnreadCountChange,
	unreadThreadIds = [],
	onUnreadThreadIdsChange,
	onMinimize,
}) => {
	const [supportStatus, setSupportStatus] = useState(null);
	const [threads, setThreads] = useState([]);
	const [broadcasts, setBroadcasts] = useState([]);
	const [messagesByThread, setMessagesByThread] = useState({});
	const [activeTicketView, setActiveTicketView] = useState(null);
	const [loading, setLoading] = useState(false);
	const [messagesLoading, setMessagesLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [toolNotice, setToolNotice] = useState('');
	const bootedForOpenRef = useRef(false);
	const lastRefreshTokenRef = useRef(refreshToken);
	const enrollmentDataRef = useRef({ profileData, pharmacyLoginData });
	const attachmentMetadataRef = useRef(new Map());

	const selectedThread = useMemo(() => {
		if (!activeTicketView || activeTicketView === 'new') return null;
		return threads.find((thread) => thread.id === activeTicketView);
	}, [threads, activeTicketView]);
	const resolvedSenderName = useMemo(
		() => getDefaultSenderName(profileData, pharmacyLoginData, senderName),
		[profileData, pharmacyLoginData, senderName],
	);

	const selectedMessages = activeTicketView && activeTicketView !== 'new'
		? (messagesByThread[activeTicketView] || [])
		: [];
	const supportEnabled = isSupportEnabled(supportStatus);
	const supportAccountReady = hasSupportAccount(supportStatus);
	const canViewCurrentFile = isTeamUser(teamStatus);

	useEffect(() => {
		enrollmentDataRef.current = { profileData, pharmacyLoginData };
	}, [profileData, pharmacyLoginData]);

	useEffect(() => {
		const handleOpenSupportChat = (event) => {
			const threadId = event?.detail?.threadId;
			if (!threadId) return;

			setActiveTicketView(threadId);
			setError('');
		};

		window.addEventListener(SUPPORT_CHAT_OPEN_EVENT, handleOpenSupportChat);
		return () => window.removeEventListener(SUPPORT_CHAT_OPEN_EVENT, handleOpenSupportChat);
	}, []);

	const rememberAttachmentMetadata = useCallback((resolvedAttachments = [], sourceAttachments = []) => {
		if (!Array.isArray(resolvedAttachments) || !resolvedAttachments.length) return;

		resolvedAttachments.forEach((attachment, index) => {
			if (!attachment?.url) return;

			const sourceAttachment = sourceAttachments[index] || {};
			const cachedAttachment = attachmentMetadataRef.current.get(attachment.url) || {};
			const nextType = getAttachmentType(sourceAttachment) || getAttachmentType(cachedAttachment) || getAttachmentType(attachment);
			const nextName = getAttachmentRawName(sourceAttachment) || getAttachmentRawName(cachedAttachment) || getAttachmentRawName(attachment);
			const nextSize = getAttachmentSize(sourceAttachment) || getAttachmentSize(cachedAttachment) || getAttachmentSize(attachment);

			attachmentMetadataRef.current.set(attachment.url, {
				url: attachment.url,
				filename: nextName || 'Attachment',
				content_type: nextType,
				size_bytes: nextSize,
			});
		});
	}, []);

	const hydrateMessageAttachments = useCallback((attachments = [], sourceAttachments = []) => {
		const normalizedAttachments = normalizeMessageAttachments(
			attachments,
			sourceAttachments,
			attachmentMetadataRef.current,
		);
		rememberAttachmentMetadata(normalizedAttachments, sourceAttachments);
		return normalizedAttachments;
	}, [rememberAttachmentMetadata]);

	const loadStatus = useCallback(async () => {
		setLoading(true);
		setError('');
		try {
			const statusResponse = await supportChatApi.getStatus();
			setSupportStatus(statusResponse);
			onSupportAvailabilityChange?.(isSupportEnabled(statusResponse));
			return statusResponse;
		} catch (requestError) {
			setError(getErrorMessage(requestError));
			return null;
		} finally {
			setLoading(false);
		}
	}, [onSupportAvailabilityChange]);

	const loadThreadsAndBroadcasts = useCallback(async () => {
		setLoading(true);
		setError('');
		try {
			const [threadResponse, broadcastResponse] = await Promise.all([
				supportChatApi.getThreads(),
				supportChatApi.getBroadcasts(),
			]);
			const nextThreads = threadResponse.threads || [];
			setThreads(nextThreads);
			setBroadcasts(broadcastResponse.broadcasts || []);
			if (threadResponse?.unread_count !== undefined && threadResponse?.unread_count !== null) {
				onUnreadCountChange?.(Number(threadResponse.unread_count || 0));
			}
			if (Array.isArray(threadResponse?.unread_thread_ids)) {
				const visibleThreadIds = new Set(nextThreads.flatMap((thread) => (
					[thread.id, thread.thread_id, thread.conversation_id, thread.conversationId, thread.uuid]
						.filter((threadId) => threadId !== undefined && threadId !== null && String(threadId).trim() !== '')
						.map((threadId) => String(threadId))
				)));
				onUnreadThreadIdsChange?.(
					threadResponse.unread_thread_ids.filter((threadId) => visibleThreadIds.has(String(threadId))),
				);
			}
		} catch (requestError) {
			setError(getErrorMessage(requestError));
		} finally {
			setLoading(false);
		}
	}, [onUnreadCountChange, onUnreadThreadIdsChange]);

	const loadMessages = useCallback(async (threadId) => {
		if (!threadId) return;

		setMessagesLoading(true);
		setError('');
		try {
			const response = await supportChatApi.getMessages(threadId);
			setMessagesByThread((current) => {
				const localMessages = (current[threadId] || []).filter((messageItem) => messageItem.local_status);
				return {
					...current,
					[threadId]: [
						...(response.messages || []).map((messageItem) => ({
							...messageItem,
							attachments: hydrateMessageAttachments(messageItem.attachments),
						})),
						...localMessages,
					],
				};
			});
			if (response?.unread_count !== undefined && response?.unread_count !== null) {
				onUnreadCountChange?.(Number(response.unread_count || 0));
			}
			if (Array.isArray(response?.unread_thread_ids)) {
				onUnreadThreadIdsChange?.(response.unread_thread_ids);
			}
		} catch (requestError) {
			setError(getErrorMessage(requestError));
		} finally {
			setMessagesLoading(false);
		}
	}, [hydrateMessageAttachments, onUnreadCountChange, onUnreadThreadIdsChange]);

	const enrollPharmacy = useCallback(async (form) => {
		setSubmitting(true);
		setError('');
		try {
			const response = await supportChatApi.enroll(form);
			onSupportAvailabilityChange?.(true);
			setSupportStatus((current) => ({
				...current,
				enabled: true,
				configured: true,
				enrolled: true,
				status: 'active',
				pharmacy_name: response.pharmacy_name || current?.pharmacy_name,
				tenant_id: response.tenant_id || current?.tenant_id,
				slug: response.slug || current?.slug,
				hub_url: response.hub_url || current?.hub_url,
			}));
			await loadThreadsAndBroadcasts();
			return true;
		} catch (requestError) {
			setError(getErrorMessage(requestError));
			return false;
		} finally {
			setSubmitting(false);
		}
	}, [loadThreadsAndBroadcasts, onSupportAvailabilityChange]);

	useEffect(() => {
		if (!isActive) {
			setError('');
		}
	}, [isActive]);

	useLayoutEffect(() => {
		if (!registerToolbar || !supportEnabled || !supportAccountReady) {
			registerToolbar?.(null);
			return undefined;
		}

		if (!isActive) {
			registerToolbar(null);
			return undefined;
		}

		registerToolbar({
			onNewThread: () => setActiveTicketView('new'),
			onRefresh: loadThreadsAndBroadcasts,
			isDetailView: Boolean(activeTicketView),
		});

		return () => registerToolbar(null);
	}, [registerToolbar, supportEnabled, supportAccountReady, isActive, loadThreadsAndBroadcasts, activeTicketView]);

	useEffect(() => {
		if (!isActive) {
			bootedForOpenRef.current = false;
			return undefined;
		}

		if (bootedForOpenRef.current) return undefined;
		bootedForOpenRef.current = true;

		let isCancelled = false;

		const boot = async () => {
			const statusResponse = await loadStatus();
			if (!statusResponse || isCancelled) return;

			if (hasSupportAccount(statusResponse)) {
				await loadThreadsAndBroadcasts();
				return;
			}

			if (isSupportEnabled(statusResponse)) {
				const {
					profileData: latestProfileData,
					pharmacyLoginData: latestPharmacyLoginData,
				} = enrollmentDataRef.current;
				const autoEnrollment = getInitialEnrollment(latestProfileData, latestPharmacyLoginData);
				if (hasRequiredEnrollmentDetails(autoEnrollment)) {
					await enrollPharmacy(autoEnrollment);
				}
			}
		};

		boot();

		return () => {
			isCancelled = true;
		};
	}, [isActive, loadStatus, loadThreadsAndBroadcasts, enrollPharmacy]);

	useEffect(() => {
		if (lastRefreshTokenRef.current === refreshToken) return;
		lastRefreshTokenRef.current = refreshToken;

		if (!isActive || !supportEnabled || !supportAccountReady) return;

		loadThreadsAndBroadcasts();
		if (activeTicketView && activeTicketView !== 'new') {
			loadMessages(activeTicketView);
		}
	}, [
		refreshToken,
		isActive,
		supportEnabled,
		supportAccountReady,
		activeTicketView,
		loadThreadsAndBroadcasts,
		loadMessages,
	]);

	const closeTicketBox = () => {
		setActiveTicketView(null);
		setError('');
	};

	const handleSelectThread = (threadId) => {
		setActiveTicketView(threadId);
		loadMessages(threadId);
	};

	const handleEnroll = async (form) => {
		await enrollPharmacy(form);
	};

	const handleCreateThread = async ({ title, message, attachments = [] }) => {
		setSubmitting(true);
		setError('');
		try {
			// Tell the support team which page the ticket was raised from.
			const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
			const response = await supportChatApi.createThread({
				title,
				message: pageUrl ? [message, `Page: ${pageUrl}`].filter(Boolean).join('\n\n') : message,
				attachments,
				sender_name: resolvedSenderName,
			});
			rememberAttachmentMetadata(response.attachments, attachments);
			await loadThreadsAndBroadcasts();
			setActiveTicketView(response.thread_id);
			await loadMessages(response.thread_id);
		} catch (requestError) {
			setError(getErrorMessage(requestError));
			throw requestError;
		} finally {
			setSubmitting(false);
		}
	};

	const handleSendMessage = async ({ message, attachments }) => {
		if (!activeTicketView || activeTicketView === 'new') return;
		const threadId = activeTicketView;
		const localMessageId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		setError('');
		setMessagesByThread((current) => ({
			...current,
			[threadId]: [
				...(current[threadId] || []),
				{
					id: localMessageId,
					direction: 'pos',
					text: message,
					attachments: toLocalMessageAttachments(attachments),
					created_at: new Date().toISOString(),
					local_status: 'sending',
					local_payload: { message, attachments },
				},
			],
		}));

		try {
			const response = await supportChatApi.sendMessage(threadId, {
				message,
				attachments,
				sender_name: resolvedSenderName,
			});
			const resolvedAttachments = hydrateMessageAttachments(response.attachments, attachments);
			setMessagesByThread((current) => ({
				...current,
				[threadId]: (current[threadId] || []).map((item) => (
					item.id === localMessageId
						? {
							id: response.message_id || localMessageId,
							local_id: localMessageId,
							direction: 'pos',
							text: message,
							attachments: resolvedAttachments,
							created_at: new Date().toISOString(),
						}
						: item
				)),
			}));
			if (response?.unread_count !== undefined && response?.unread_count !== null) {
				onUnreadCountChange?.(Number(response.unread_count || 0));
			}
			if (Array.isArray(response?.unread_thread_ids)) {
				onUnreadThreadIdsChange?.(response.unread_thread_ids);
			}
			await loadThreadsAndBroadcasts();
		} catch (requestError) {
			setError(getErrorMessage(requestError));
			setMessagesByThread((current) => ({
				...current,
				[threadId]: (current[threadId] || []).map((item) => (
					item.id === localMessageId
						? {
							...item,
							local_status: 'failed',
							local_error: getErrorMessage(requestError),
						}
						: item
				)),
			}));
		}
	};

	const handleRetryMessage = async (messageItem) => {
		if (!activeTicketView || activeTicketView === 'new' || !messageItem?.local_payload) return;

		const threadId = activeTicketView;
		const localMessageId = messageItem.id;
		const payload = messageItem.local_payload;
		setError('');
		setMessagesByThread((current) => ({
			...current,
			[threadId]: (current[threadId] || []).map((item) => (
				item.id === localMessageId
					? { ...item, local_status: 'sending', local_error: '' }
					: item
			)),
		}));

		try {
			const response = await supportChatApi.sendMessage(threadId, {
				message: payload.message,
				attachments: payload.attachments,
				sender_name: resolvedSenderName,
			});
			const resolvedAttachments = hydrateMessageAttachments(response.attachments, payload.attachments);
			setMessagesByThread((current) => ({
				...current,
				[threadId]: (current[threadId] || []).map((item) => (
					item.id === localMessageId
						? {
							id: response.message_id || localMessageId,
							local_id: localMessageId,
							direction: 'pos',
							text: payload.message,
							attachments: resolvedAttachments,
							created_at: new Date().toISOString(),
						}
						: item
				)),
			}));
			if (response?.unread_count !== undefined && response?.unread_count !== null) {
				onUnreadCountChange?.(Number(response.unread_count || 0));
			}
			if (Array.isArray(response?.unread_thread_ids)) {
				onUnreadThreadIdsChange?.(response.unread_thread_ids);
			}
			await loadThreadsAndBroadcasts();
		} catch (requestError) {
			setError(getErrorMessage(requestError));
			setMessagesByThread((current) => ({
				...current,
				[threadId]: (current[threadId] || []).map((item) => (
					item.id === localMessageId
						? {
							...item,
							local_status: 'failed',
							local_error: getErrorMessage(requestError),
						}
						: item
				)),
			}));
		}
	};

	const handleCaptureSnapshot = async () => {
		if (!navigator.mediaDevices?.getDisplayMedia) {
			showToolNotice('Snapshot capture is not supported in this browser.');
			throw new Error('Snapshot capture is not supported in this browser.');
		}

		let stream;
		try {
			stream = await navigator.mediaDevices.getDisplayMedia({
				audio: false,
				preferCurrentTab: true,
				video: {
					cursor: 'always',
					displaySurface: 'browser',
					logicalSurface: true,
				},
			});

			return await streamToSnapshotFile(stream, `page-snapshot-${Date.now()}.png`);
		} catch (captureError) {
			showToolNotice('Snapshot was not captured. Please choose the current tab when prompted.');
			throw captureError;
		} finally {
			stream?.getTracks?.().forEach((track) => track.stop());
		}
	};

	const showToolNotice = (message) => {
		setToolNotice(message);
		window.setTimeout(() => setToolNotice(''), 4000);
	};

	if (!supportStatus) {
		return (
			<div className="support-chat-panel">
				<div className="support-chat-empty centered">
					<LifeBuoy size={28} />
					<p>{error || 'Loading support...'}</p>
				</div>
			</div>
		);
	}

	if (supportStatus && !supportEnabled) {
		return (
			<div className="support-chat-panel">
				<div className="support-chat-empty centered">
					<LifeBuoy size={28} />
					<p>Support chat is not enabled from backend settings.</p>
				</div>
			</div>
		);
	}

	if (!supportAccountReady) {
		return (
			<div className="support-chat-panel">
				<EnrollmentForm
					profileData={profileData}
					pharmacyLoginData={pharmacyLoginData}
					loading={submitting}
					error={error}
					onSubmit={handleEnroll}
				/>
			</div>
		);
	}

	return (
		<div className={`support-chat-panel ${activeTicketView ? 'support-chat-panel--detail' : ''}`}>
			{toolNotice && <div className="support-chat-tool-notice">{toolNotice}</div>}

			{!activeTicketView && (
				<ThreadList
					threads={threads}
					broadcasts={broadcasts}
					activeThreadId={null}
					onSelectThread={handleSelectThread}
					loading={loading}
					unreadThreadIds={unreadThreadIds}
				/>
			)}

			{activeTicketView === 'new' && (
				<>
					<SupportViewHeader title="New ticket" onBack={closeTicketBox} onMinimize={onMinimize} />
					<div className="support-chat-detail-body">
						<NewThreadForm
							loading={submitting}
							error={error}
							onSubmit={handleCreateThread}
							onCaptureSnapshot={handleCaptureSnapshot}
							onToolNotice={showToolNotice}
							canViewCurrentFile={canViewCurrentFile}
						/>
					</div>
				</>
			)}

			{activeTicketView && activeTicketView !== 'new' && selectedThread && (
				<>
					<SupportViewHeader
						title={selectedThread.title || 'Support ticket'}
						status={selectedThread.status === 'resolved' ? 'closed' : 'open'}
						onBack={closeTicketBox}
						onMinimize={onMinimize}
					/>
					<div className="support-chat-detail-body">
						<MessagePane
							thread={selectedThread}
							messages={selectedMessages}
							loading={messagesLoading && selectedMessages.length === 0}
							error={error}
							onSend={handleSendMessage}
							onRetryMessage={handleRetryMessage}
							onCaptureSnapshot={handleCaptureSnapshot}
							onToolNotice={showToolNotice}
							canViewCurrentFile={canViewCurrentFile}
						/>
					</div>
				</>
			)}

			{activeTicketView && activeTicketView !== 'new' && !selectedThread && (
				<div className="support-chat-loading">Loading ticket...</div>
			)}
		</div>
	);
};

export default SupportChatPanel;
